import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  Mail, MessageCircle, Users, Zap, QrCode, UserPlus,
  ArrowRight,
} from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import AutomationsMap from '@/components/AutomationsMap';

/**
 * S176/S179 — Inicio de Marketing (captación). Email/WA masivos viven en su menú.
 * Textos 100% vía i18n (es/en/de/pt).
 */
export default function MarketingHome() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tenant/marketing/simple-status').then(r => setStats(r.data)).catch(() => null).finally(() => setLoading(false));
  }, []);

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-6" data-testid="marketing-home">

      <AutomationsMap />
      <div>
        <h2 className="text-base font-semibold text-[#0F172A]">{t('mkt.whatToday')}</h2>
        <p className="text-sm text-[#64748B] mt-1">{t('mkt.whatTodaySub')}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ActionCard
          icon={QrCode}
          color="#0F172A"
          title={t('mkt.originsTitle')}
          subtitle={t('mkt.originsSub')}
          points={[t('mkt.originsP1'), t('mkt.originsP2'), t('mkt.originsP3')]}
          cta={t('mkt.originsCta')}
          onClick={() => navigate('/tenant/marketing/origenes')}
          testId="marketing-home-qr"
          featured
        />
        <ActionCard
          icon={UserPlus}
          color="#F72585"
          title={t('mkt.audienceTitle')}
          subtitle={t('mkt.audienceSub')}
          points={[t('mkt.audienceP1'), t('mkt.audienceP2'), t('mkt.audienceP3')]}
          cta={t('mkt.audienceCta')}
          onClick={() => navigate('/tenant/marketing/origenes')}
          testId="marketing-home-segments-card"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <MiniCard
          icon={Users}
          title={t('mkt.segmentsTitle')}
          text={t('mkt.segmentsText')}
          onClick={() => navigate('/tenant/marketing/segments')}
        />
        <MiniCard
          icon={Zap}
          title={t('mkt.autoTitle')}
          text={t('mkt.autoText')}
          onClick={() => navigate('/tenant/marketing/automations')}
        />
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
        <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-3">
          {t('mkt.massSection')}
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate('/tenant/email')}
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#F72585]/40 text-left"
            data-testid="marketing-home-go-email"
          >
            <div className="w-10 h-10 rounded-xl bg-[#FFF5F9] flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#F72585]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0F172A]">{t('mkt.emailMass')}</p>
              <p className="text-[11px] text-[#64748B]">{t('mkt.emailMassSub')}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
          </button>
          <button
            type="button"
            onClick={() => navigate('/tenant/whatsapp')}
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#128C7E]/50 text-left"
            data-testid="marketing-home-go-wa"
          >
            <div className="w-10 h-10 rounded-xl bg-[#ECFDF5] flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#128C7E]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0F172A]">{t('mkt.waMass')}</p>
              <p className="text-[11px] text-[#64748B]">{t('mkt.waMassSub')}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="marketing-home-stats">
          <Stat label={t('mkt.statLeads')} value={stats.contacts_count} />
          <Stat label={t('mkt.statTpl')} value={stats.templates_approved_count} />
          <Stat label={t('mkt.statDrafts')} value={stats.campaigns_draft_count} />
          <Stat label={t('mkt.statAuto')} value={stats.automations_active_count} />
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, color, title, subtitle, points, cta, onClick, testId, featured }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-2xl border p-5 transition-all hover:shadow-md ${
        featured
          ? 'border-[#0F172A]/15 bg-gradient-to-br from-white to-[#F8FAFC]'
          : 'border-[#E2E8F0] bg-white'
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${color}12` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-base font-bold text-[#0F172A]">{title}</p>
          <p className="text-xs text-[#64748B] mt-0.5">{subtitle}</p>
        </div>
      </div>
      <ul className="space-y-1 mb-4">
        {points.map((p) => (
          <li key={p} className="text-xs text-[#475569] flex gap-2">
            <span className="text-[#F72585]">·</span> {p}
          </li>
        ))}
      </ul>
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#F72585]">
        {cta} <ArrowRight className="w-4 h-4" />
      </span>
    </button>
  );
}

function MiniCard({ icon: Icon, title, text, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-start gap-3 p-4 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#F72585]/30 text-left"
    >
      <div className="w-9 h-9 rounded-lg bg-[#FFF5F9] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#F72585]" />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#0F172A]">{title}</p>
        <p className="text-[11px] text-[#64748B] mt-0.5">{text}</p>
      </div>
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{label}</p>
      <p className="text-lg font-bold text-[#0F172A]">{value ?? '—'}</p>
    </div>
  );
}
